/**
 * Queries + mutations do subsistema de Propostas.
 *
 * **PR-M1-01c-i** — 2 mutations para IA + criação:
 *   - `useSuggestProposalDraft` — chama edge function que invoca AI
 *     Gateway (módulo `proposal_draft`) e valida output com Zod
 *   - `useCreateProposal` — persiste rascunho em `public.proposals`
 *     + registra activity no lead
 *
 * **PR-M1-01c-iii.a+.b** — listar + editar + excluir rascunhos:
 *   - `useLeadProposals(leadId)` — lista rascunhos/propostas do lead
 *   - `useUpdateProposalDraft` — UPDATE direto (RLS superadmin aceita)
 *   - `useDeleteProposal` — soft delete
 *
 * **PR-M1-01c-ii (próximo)** vai adicionar:
 *   - `useSendProposal` — transitar draft→sent + disparar email + gerar token
 *   - `usePublicProposal(token)` — rota pública /proposta/:token
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { supabase } from '../../lib/supabase.js';

import { z } from 'zod';

import {
  createProposalInputSchema,
  createProposalResponseSchema,
  leadProposalSummarySchema,
  suggestProposalDraftResponseSchema,
  type CreateProposalInput,
  type CreateProposalResponse,
  type LeadProposalSummary,
  type SuggestProposalDraftResponse,
} from './schemas.js';

// ─── read-public-proposal (rota pública /proposta/:token) ──────────
//
// Endpoint público sem JWT — token é o segredo. Edge function marca
// viewed_at na primeira leitura (side effect benigno). Retorna
// proposal + lead minimal + platform_settings consolidados pro
// ProposalPublicView consumir direto.
const publicProposalLeadSchema = z.object({
  full_name: z.string(),
  clinic_name_guess: z.string().nullable(),
});

const platformSettingsPublicSchema = z.object({
  contratada: z.object({
    razao_social: z.string(),
    cnpj: z.string(),
    nome_fantasia: z.string(),
    cidade_uf: z.string(),
    email_contato: z.string(),
    responsavel_nome: z.string(),
    responsavel_cargo: z.string(),
  }),
  proposal_footer: z.object({
    institutional_text: z.string(),
  }),
  // Adicionado no PR-M1-01c-ii.j (PR B, 2026-04-24): textos longos do
  // ProposalPublicView (hero, sobre-nós, considerações bullets) agora
  // vêm do banco em vez de hardcoded. `.default({})` + campos opcionais
  // com string default garantem que caches velhos (sem essas chaves)
  // continuem parseando sem crash — UI cai em defaults razoáveis.
  proposal_copy: z
    .object({
      hero_title: z.string().default('Olá, é um prazer'),
      hero_intro_template: z.string().default(''),
      about_us_title: z.string().default('Sobre nós'),
      about_us_paragraphs: z.string().default(''),
      considerations_bullets: z.string().default(''),
      post_consulting_terms_template: z.string().default(''),
    })
    .default({
      hero_title: 'Olá, é um prazer',
      hero_intro_template: '',
      about_us_title: 'Sobre nós',
      about_us_paragraphs: '',
      considerations_bullets: '',
      post_consulting_terms_template: '',
    }),
});

// Contract info exposta ao lead quando proposta aceita (PR-M1-01c-ii.k-2b).
// `null` quando Karla ainda não clicou "Gerar contrato".
const publicContractSchema = z
  .object({
    id: z.string(),
    approval_status: z.enum(['pending', 'approved', 'adjustment_requested']),
    approved_at: z.string().nullable(),
    has_pdf: z.boolean(),
    pdf_signed_url: z.string().nullable(),
    revisions_count: z.number(),
    revisions: z.array(
      z.object({
        id: z.string(),
        requested_at: z.string(),
        text: z.string(),
        responded_at: z.string().nullable(),
      }),
    ),
  })
  .nullable();

const publicProposalResponseSchema = z.object({
  mode: z.enum(['live', 'stub']),
  // O handler `read-public-proposal` devolve sempre a mesma forma de
  // `LeadProposalSummary` (embed plan_key/plan_name_pt_br + plan_id)
  // — mantemos o schema idêntico pra `ProposalPublicView` consumir
  // sem cast e o TS reclamar com `exactOptionalPropertyTypes`.
  proposal: leadProposalSummarySchema.passthrough(),
  lead: publicProposalLeadSchema,
  // Campo opcional — stub mode não retorna; só presente em live quando
  // proposta aceita.
  contract: publicContractSchema.optional().default(null),
  platform_settings: platformSettingsPublicSchema,
});

export type PublicProposalResponse = z.infer<typeof publicProposalResponseSchema>;

export const PUBLIC_PROPOSAL_KEYS = {
  byToken: (token: string) => ['proposals', 'public', token] as const,
};

/** Erros de rede/infra do Supabase Edge Functions — cold start ocasional,
 * 503 transiente, falha no relay. Vistos em prod 2026-04-25 quando lead
 * caiu na tela "Link não encontrado" mesmo com token válido. Identificar
 * pra retry agressivo (3 tentativas com backoff), em vez de cair no
 * caminho de erro permanente. */
function isPublicProposalRetryable(error: Error): boolean {
  const msg = error.message.toLowerCase();
  return (
    msg.includes('failed to send a request') ||
    msg.includes('failed to fetch') ||
    msg.includes('503') ||
    msg.includes('502') ||
    msg.includes('504') ||
    msg.includes('network') ||
    msg.includes('timeout')
  );
}

export function usePublicProposal(
  token: string | undefined,
  options?: Partial<UseQueryOptions<PublicProposalResponse, Error>>,
) {
  return useQuery<PublicProposalResponse, Error>({
    queryKey: PUBLIC_PROPOSAL_KEYS.byToken(token ?? ''),
    enabled: Boolean(token),
    staleTime: 30_000,
    // Retry só em erro transiente (rede/5xx). Token inválido vem do edge
    // como erro com mensagem específica e cai no else → 0 retries → mostra
    // "link não encontrado" imediatamente.
    retry: (failureCount, error) => failureCount < 3 && isPublicProposalRetryable(error),
    retryDelay: (attempt) => Math.min(800 * 2 ** attempt, 4_000), // 800ms, 1.6s, 3.2s
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('read-public-proposal', {
        body: { token },
      });
      if (error) throw new Error(error.message);
      return publicProposalResponseSchema.parse(data);
    },
    ...options,
  });
}

// ─── send-proposal (transita draft → sent) ──────────────────────────
const sendProposalResponseSchema = z.object({
  mode: z.enum(['live', 'stub']),
  proposal_id: z.string(),
  public_url: z.string().url(),
  public_token: z.string(),
  sent_at: z.string(),
  email_status: z.enum(['sent', 'skipped', 'failed']),
});

export type SendProposalResponse = z.infer<typeof sendProposalResponseSchema>;

// ─── Keys ────────────────────────────────────────────────────────────
export const PROPOSALS_KEYS = {
  all: () => ['proposals'] as const,
  byLead: (leadId: string) => ['proposals', 'by-lead', leadId] as const,
  detail: (proposalId: string) => ['proposals', 'detail', proposalId] as const,
};

// ─── suggest-proposal-draft (chamada via IA) ────────────────────────

export interface SuggestProposalDraftInput {
  lead_id: string;
  context: string;
  highlights: string;
  include_questionnaire_answers: boolean;
  /** Preferências estruturais já configuradas por Karla no form.
   * Envio sempre por padrão para que a IA respeite plano/modo/preço/
   * consultoria ao redigir `notes_pt_br`. Inclui **contexto completo
   * da proposta** (PR-M1-01c-ii.l, 2026-04-24): antes só tinha SaaS,
   * IA ignorava consultoria no combo. Agora recebe todos os campos
   * e a Regra #2 do prompt proíbe citar valores (deixa pros cards).
   * Ver `suggest-proposal-draft/prompt-builder.ts`. */
  user_preferences?: {
    proposal_type: 'saas' | 'consulting' | 'saas_consulting';
    // SaaS
    // String livre (qualquer key de public.plans) desde fix/proposal-plan-dropdown.
    plan_key: string | null;
    price_mode: 'discount' | 'custom' | null;
    discount_percent: number | null;
    custom_price_brl: number | null;
    trial_days: number;
    valid_until_days: number;
    // Consultoria
    consulting_total_brl: number | null;
    consulting_installments_count: number | null;
    consulting_installment_brl: number | null;
    consulting_duration_months: number | null;
    consulting_weekly_hours: number | null;
    consulting_extra_hour_brl: number | null;
    consulting_modality: 'presencial' | 'hibrido' | 'online' | null;
    // Combo
    saas_free_during_consulting: boolean | null;
  };
}

/** Helper que chama a edge function uma vez. Separado pra reutilizar
 * no retry. */
async function callSuggestProposalDraft(
  input: SuggestProposalDraftInput,
): Promise<SuggestProposalDraftResponse> {
  const { data, error } = await supabase.functions.invoke('suggest-proposal-draft', {
    body: input,
  });
  if (error) {
    const ctx =
      (error as unknown as { context?: { body?: unknown; status?: number } }).context ?? {};
    const status = typeof ctx.status === 'number' ? ctx.status : undefined;
    const contextBody = ctx.body ?? null;
    const parsed =
      typeof contextBody === 'string'
        ? (() => {
            try {
              return JSON.parse(contextBody);
            } catch {
              return null;
            }
          })()
        : null;
    const detailedReason =
      parsed && typeof parsed === 'object' && 'reason' in parsed
        ? String((parsed as { reason: unknown }).reason)
        : error.message;
    const err = new Error(detailedReason) as Error & { status?: number };
    if (status !== undefined) err.status = status;
    throw err;
  }
  return suggestProposalDraftResponseSchema.parse(data);
}

/** Retorna true pra erros transientes que valem retry (timeout 502,
 * bad gateway 504, rede). Diferencia de 400/401/500 que não adiantam
 * retry. */
function isTransientAiError(err: Error & { status?: number }): boolean {
  const status = err.status;
  if (status === 502 || status === 503 || status === 504 || status === 408) return true;
  // Mensagens típicas quando o edge function falha sem status explícito.
  const msg = err.message.toLowerCase();
  return (
    msg.includes('timeout') ||
    msg.includes('failed to fetch') ||
    msg.includes('network') ||
    msg.includes('gateway')
  );
}

/** Aguarda `ms` milissegundos. */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function useSuggestProposalDraft(
  options?: UseMutationOptions<SuggestProposalDraftResponse, Error, SuggestProposalDraftInput>,
) {
  return useMutation<SuggestProposalDraftResponse, Error, SuggestProposalDraftInput>({
    mutationFn: async (input) => {
      // Retry automático com backoff em erros transientes (502/timeout).
      // PR-M1-01c-ii.n (2026-04-24): Alexandre reportou 502 após 60s na
      // primeira tentativa e pediu "não pode persistir mesmo 1 vez".
      // Estratégia: 1ª tentativa + até 2 retries com 1.5s e 3s de espera.
      // Backend já tem max_retries=2 entre primary/fallback model;
      // este retry cobre quando o próprio edge function timeouta.
      const MAX_ATTEMPTS = 3;
      const BACKOFFS_MS = [1_500, 3_000];
      let lastError: Error | null = null;
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          return await callSuggestProposalDraft(input);
        } catch (err) {
          const error = err as Error & { status?: number };
          lastError = error;
          if (attempt === MAX_ATTEMPTS || !isTransientAiError(error)) {
            throw error;
          }
          const delayMs = BACKOFFS_MS[attempt - 1] ?? 3_000;

          console.warn(
            `[useSuggestProposalDraft] tentativa ${attempt}/${MAX_ATTEMPTS} falhou (${error.message}). Tentando de novo em ${delayMs}ms…`,
          );
          await sleep(delayMs);
        }
      }
      // Unreachable — MAX_ATTEMPTS sempre throws no loop. TS não infere.
      throw lastError ?? new Error('Falha desconhecida ao gerar rascunho.');
    },
    ...options,
  });
}

// ─── create-proposal (persist) ───────────────────────────────────────

export function useCreateProposal(
  options?: UseMutationOptions<CreateProposalResponse, Error, CreateProposalInput>,
) {
  const qc = useQueryClient();
  return useMutation<CreateProposalResponse, Error, CreateProposalInput>({
    mutationFn: async (input) => {
      // Validação dupla (cliente + servidor) — Zod falha mais cedo
      // com mensagem amigável em vez de 400 do backend.
      const parsed = createProposalInputSchema.parse(input);
      const { data, error } = await supabase.functions.invoke('create-proposal', {
        body: parsed,
      });
      if (error) throw new Error(error.message);
      return createProposalResponseSchema.parse(data);
    },
    onSuccess: (_data, variables) => {
      // Invalida listagem do lead para que a nova proposta apareça
      // imediatamente na seção "Propostas" do detalhe.
      qc.invalidateQueries({ queryKey: PROPOSALS_KEYS.byLead(variables.lead_id) });
    },
    ...options,
  });
}

// ─── Lista de propostas do lead (PR-M1-01c-iii.a+.b) ────────────────
//
// SELECT em `public.proposals` + embed do plano para pegar name_pt_br
// e key sem query extra. Filtra deleted_at para não mostrar excluídas.
// Ordenação: mais recente primeiro.
//
// RLS: `proposals_all_superadmin` permite SELECT irrestrito para
// superadmin; nenhum outro role alcança a tabela. Invariante OK: só
// Superadmin vê propostas do lead (clínica só vê após contrato assinado
// → provisionamento, e aí já está em `clinic_id` da própria clínica).
export function useLeadProposals(
  leadId: string | undefined,
  options?: Partial<UseQueryOptions<LeadProposalSummary[], Error>>,
) {
  return useQuery<LeadProposalSummary[], Error>({
    queryKey: PROPOSALS_KEYS.byLead(leadId ?? ''),
    enabled: Boolean(leadId),
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('proposals')
        .select(
          `
            id, proposal_type, status, plan_id, discount_percent, custom_price_brl, trial_days,
            consulting_installments_count, consulting_installment_brl, consulting_duration_months,
            consulting_weekly_hours, consulting_extra_hour_brl, consulting_scope_notes,
            consulting_modality, consulting_payment_method,
            saas_free_during_consulting,
            valid_until, notes, public_token,
            sent_at, viewed_at, accepted_at, rejected_at, questioning_message,
            ai_context, ai_highlights,
            created_at, updated_at,
            plan:plans ( key, name_pt_br )
          `,
        )
        .eq('lead_id', leadId ?? '')
        .order('created_at', { ascending: false });

      if (error) throw new Error(error.message);

      // Flatten plan embed → plan_key + plan_name_pt_br (facilita consumir
      // sem precisar tratar nested no JSX).
      const normalized = (data ?? []).map((row) => {
        const rec = row as unknown as Record<string, unknown> & {
          plan?: { key?: string; name_pt_br?: string } | null;
        };
        const planEmbed = rec.plan;
        return {
          ...rec,
          plan_key: (planEmbed?.key ?? null) as LeadProposalSummary['plan_key'],
          plan_name_pt_br: planEmbed?.name_pt_br ?? null,
        };
      });

      return leadProposalSummarySchema.array().parse(normalized);
    },
    ...options,
  });
}

// ─── Atualizar proposta editável ────────────────────────────────────
//
// UPDATE direto via supabase client — RLS `proposals_all_superadmin`
// aceita. Aceita qualquer subset dos campos editáveis.
//
// **Status editáveis** (ampliado em PR-M1-01c-ii.i, 2026-04-24):
//   - `draft`: primeira edição antes do envio inicial
//   - `sent`: Karla percebeu algo após enviar, quer corrigir e reenviar
//   - `viewed`: lead abriu mas Karla quer ajustar após conversa off-app
//   - `in_negotiation`: edição pós-dúvida do lead (Karla ajusta em
//     resposta e reenvia). Após reenvio status volta pra `sent` via
//     `send-proposal` handler, preservando o `public_token`.
//
// `accepted/rejected/expired` continuam bloqueados — proposta fechou.
// Alexandre 2026-04-24: "Pode ser que converse com o médico via outros
// canais, ele me peça algum ajuste e eu quero editar e enviar novamente".
const EDITABLE_STATUSES = ['draft', 'sent', 'viewed', 'in_negotiation'] as const;

export interface UpdateProposalDraftInput {
  id: string;
  patch: Partial<Omit<CreateProposalInput, 'lead_id' | 'proposal_type'>> & {
    /** Data de validade recalculada via valid_until_days no dialog. */
    valid_until?: string;
  };
  /** Lead id apenas para invalidar cache certo — não é atualizado. */
  lead_id: string;
}

export function useUpdateProposalDraft() {
  const qc = useQueryClient();
  return useMutation<{ id: string }, Error, UpdateProposalDraftInput>({
    mutationFn: async (input) => {
      const { data, error } = await supabase
        .from('proposals')
        .update(input.patch as never)
        .eq('id', input.id)
        .in('status', EDITABLE_STATUSES as unknown as string[])
        .select('id')
        .single();
      if (error) throw new Error(error.message);
      if (!data) {
        throw new Error(
          'Proposta não encontrada ou já fechada (aceita/recusada/expirada não podem ser editadas).',
        );
      }
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: PROPOSALS_KEYS.byLead(variables.lead_id) });
    },
  });
}

// ─── Enviar proposta (draft → sent) ──────────────────────────────────
//
// Chama a edge function `send-proposal` que gera token público, transita
// status, transita lead.stage (quando allowed) e dispara email.
// Invalida byLead para que a lista de propostas refresque (status vira
// "Enviada", aparece `sent_at`).
export interface SendProposalInput {
  proposal_id: string;
  lead_id: string;
}

export function useSendProposal(
  options?: UseMutationOptions<SendProposalResponse, Error, SendProposalInput>,
) {
  const qc = useQueryClient();
  return useMutation<SendProposalResponse, Error, SendProposalInput>({
    mutationFn: async (input) => {
      const { data, error } = await supabase.functions.invoke('send-proposal', {
        body: { proposal_id: input.proposal_id },
      });
      if (error) throw new Error(error.message);
      return sendProposalResponseSchema.parse(data);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: PROPOSALS_KEYS.byLead(variables.lead_id) });
      // Lead também teve stage atualizada → refresh do detail.
      qc.invalidateQueries({ queryKey: ['leads', 'detail', variables.lead_id] });
      qc.invalidateQueries({ queryKey: ['leads', 'activities', variables.lead_id] });
    },
    ...options,
  });
}

// ─── create-contract (Karla clica "Gerar contrato") ───────────────
//
// Dispara a edge function create-contract, que: (1) insere linha em
// `contracts` status=draft; (2) chama `generate-contract-pdf` pra
// renderizar PDF real a partir dos templates em platform_settings;
// (3) o lead aprova na rota pública e `approve-contract` cria o envelope
// Assinafy. Idempotente — se contract já existe pra proposal, retorna
// cached. PR-M1-01c-ii.k-2a.
export interface GenerateContractInput {
  proposal_id: string;
  lead_id: string; // pra invalidar queries do detail após sucesso
  template_version?: string; // default 'v1'
}

const generateContractResponseSchema = z
  .object({
    status: z.string().optional(),
    mode: z.string().optional(),
    contract_id: z.string().nullable().optional(),
    envelope_id: z.string().optional(),
    signature_url: z.string().nullable().optional(),
    email_status: z.enum(['sent', 'failed', 'skipped']).optional(),
    reused: z.boolean().optional(),
  })
  .passthrough();

export type GenerateContractResponse = z.infer<typeof generateContractResponseSchema>;

export function useGenerateContract(
  options?: UseMutationOptions<GenerateContractResponse, Error, GenerateContractInput>,
) {
  const qc = useQueryClient();
  return useMutation<GenerateContractResponse, Error, GenerateContractInput>({
    mutationFn: async (input) => {
      const { data, error } = await supabase.functions.invoke('create-contract', {
        body: {
          proposal_id: input.proposal_id,
          template_version: input.template_version ?? 'v1',
          provider: 'assinafy',
        },
      });
      if (error) throw new Error(error.message);
      return generateContractResponseSchema.parse(data);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: PROPOSALS_KEYS.byLead(variables.lead_id) });
      qc.invalidateQueries({ queryKey: ['leads', 'detail', variables.lead_id] });
      qc.invalidateQueries({ queryKey: ['leads', 'activities', variables.lead_id] });
    },
    ...options,
  });
}

// ─── accept-proposal (lead aceita via rota pública) ────────────────
//
// Dispara a edge function que transita status → accepted, grava
// accepted_at, limpa pre_negotiation_snapshot e move lead.stage.
// PR-M1-01f (2026-04-24). Integração ZapSign fica pro próximo PR.
export interface AcceptProposalInput {
  token: string;
}

const acceptProposalResponseSchema = z.object({
  mode: z.enum(['live', 'stub']),
  proposal_id: z.string(),
  accepted_at: z.string(),
  email_status: z.enum(['sent', 'skipped', 'failed']).optional(),
});

export type AcceptProposalResponse = z.infer<typeof acceptProposalResponseSchema>;

export function useAcceptProposal(
  options?: UseMutationOptions<AcceptProposalResponse, Error, AcceptProposalInput>,
) {
  const qc = useQueryClient();
  return useMutation<AcceptProposalResponse, Error, AcceptProposalInput>({
    mutationFn: async (input) => {
      const { data, error } = await supabase.functions.invoke('accept-proposal', {
        body: input,
      });
      if (error) {
        const contextBody =
          (error as unknown as { context?: { body?: unknown } }).context?.body ?? null;
        const parsed =
          typeof contextBody === 'string'
            ? (() => {
                try {
                  return JSON.parse(contextBody);
                } catch {
                  return null;
                }
              })()
            : null;
        const detailedReason =
          parsed && typeof parsed === 'object' && 'reason' in parsed
            ? String((parsed as { reason: unknown }).reason)
            : error.message;
        throw new Error(detailedReason);
      }
      return acceptProposalResponseSchema.parse(data);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: PUBLIC_PROPOSAL_KEYS.byToken(variables.token) });
    },
    ...options,
  });
}

// ─── submit-proposal-question (lead envia dúvida antes de aceitar) ──
//
// Chamada pública da rota `/proposta/:token`. Usa `supabase.functions.invoke`
// com `--no-verify-jwt` no backend (mesmo padrão de `read-public-proposal`).
// Após sucesso, a query `usePublicProposal` é invalidada pra re-buscar e
// renderizar o banner "sua dúvida foi registrada" (status agora in_negotiation).
export interface SubmitProposalQuestionInput {
  token: string;
  message: string;
}

const submitProposalQuestionResponseSchema = z.object({
  mode: z.enum(['live', 'stub']),
  proposal_id: z.string(),
  recorded_at: z.string(),
  email_status: z.enum(['sent', 'skipped', 'failed']).optional(),
});

export type SubmitProposalQuestionResponse = z.infer<typeof submitProposalQuestionResponseSchema>;

export function useSubmitProposalQuestion(
  options?: UseMutationOptions<SubmitProposalQuestionResponse, Error, SubmitProposalQuestionInput>,
) {
  const qc = useQueryClient();
  return useMutation<SubmitProposalQuestionResponse, Error, SubmitProposalQuestionInput>({
    mutationFn: async (input) => {
      const { data, error } = await supabase.functions.invoke('submit-proposal-question', {
        body: input,
      });
      if (error) {
        // Extrai `reason` do corpo JSON quando o gateway devolve 4xx/5xx
        // — mesmo padrão do `useSuggestProposalDraft`.
        const contextBody =
          (error as unknown as { context?: { body?: unknown } }).context?.body ?? null;
        const parsed =
          typeof contextBody === 'string'
            ? (() => {
                try {
                  return JSON.parse(contextBody);
                } catch {
                  return null;
                }
              })()
            : null;
        const detailedReason =
          parsed && typeof parsed === 'object' && 'reason' in parsed
            ? String((parsed as { reason: unknown }).reason)
            : error.message;
        throw new Error(detailedReason);
      }
      return submitProposalQuestionResponseSchema.parse(data);
    },
    onSuccess: (_data, variables) => {
      // Invalida a query pública pra re-buscar proposta com o novo status
      // e `questioning_message` populado.
      qc.invalidateQueries({ queryKey: PUBLIC_PROPOSAL_KEYS.byToken(variables.token) });
    },
    ...options,
  });
}

// ─── approve-contract (lead aprova contrato via rota pública) ─────
//
// Chamado quando lead clica "Aprovar contrato" na página /proposta/:token.
// Move contract.approval_status pra 'approved', seta approved_at. Dispara
// (no futuro, via 01f.b) envelope Assinafy. Idempotente — retry após
// approved retorna { already_approved: true }. PR-M1-01c-ii.k-2b.
export interface ApproveContractInput {
  token: string;
}

const approveContractResponseSchema = z
  .object({
    ok: z.boolean(),
    stub: z.boolean().optional(),
    contract_id: z.string().nullable(),
    approved_at: z.string().nullable(),
    signature_url: z.string().nullable(),
    already_approved: z.boolean().optional(),
    next_step: z.string().optional(),
  })
  .passthrough();

export type ApproveContractResponse = z.infer<typeof approveContractResponseSchema>;

export function useApproveContract(
  options?: UseMutationOptions<ApproveContractResponse, Error, ApproveContractInput>,
) {
  const qc = useQueryClient();
  return useMutation<ApproveContractResponse, Error, ApproveContractInput>({
    mutationFn: async (input) => {
      const { data, error } = await supabase.functions.invoke('approve-contract', {
        body: input,
      });
      if (error) {
        const ctx = (error as unknown as { context?: { body?: unknown } }).context ?? {};
        const body = ctx.body ?? null;
        const parsed =
          typeof body === 'string'
            ? (() => {
                try {
                  return JSON.parse(body);
                } catch {
                  return null;
                }
              })()
            : null;
        const reason =
          parsed && typeof parsed === 'object' && 'reason' in parsed
            ? String((parsed as { reason: unknown }).reason)
            : error.message;
        throw new Error(reason);
      }
      return approveContractResponseSchema.parse(data);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: PUBLIC_PROPOSAL_KEYS.byToken(variables.token) });
    },
    ...options,
  });
}

// ─── request-contract-adjustment (lead solicita mudança) ──────────
//
// Chamado quando lead clica "Solicitar ajuste" + preenche textarea. Append
// em contract_revisions, seta approval_status='adjustment_requested',
// notifica Karla. Bloqueia duplo pedido — só 1 ajuste pendente por vez.
export interface RequestContractAdjustmentInput {
  token: string;
  adjustment_text: string;
}

const requestContractAdjustmentResponseSchema = z
  .object({
    ok: z.boolean(),
    stub: z.boolean().optional(),
    contract_id: z.string().nullable(),
    revision_id: z.string(),
    next_step: z.string().optional(),
  })
  .passthrough();

export type RequestContractAdjustmentResponse = z.infer<
  typeof requestContractAdjustmentResponseSchema
>;

export function useRequestContractAdjustment(
  options?: UseMutationOptions<
    RequestContractAdjustmentResponse,
    Error,
    RequestContractAdjustmentInput
  >,
) {
  const qc = useQueryClient();
  return useMutation<RequestContractAdjustmentResponse, Error, RequestContractAdjustmentInput>({
    mutationFn: async (input) => {
      const { data, error } = await supabase.functions.invoke('request-contract-adjustment', {
        body: input,
      });
      if (error) {
        const ctx = (error as unknown as { context?: { body?: unknown } }).context ?? {};
        const body = ctx.body ?? null;
        const parsed =
          typeof body === 'string'
            ? (() => {
                try {
                  return JSON.parse(body);
                } catch {
                  return null;
                }
              })()
            : null;
        const reason =
          parsed && typeof parsed === 'object' && 'reason' in parsed
            ? String((parsed as { reason: unknown }).reason)
            : error.message;
        throw new Error(reason);
      }
      return requestContractAdjustmentResponseSchema.parse(data);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: PUBLIC_PROPOSAL_KEYS.byToken(variables.token) });
    },
    ...options,
  });
}

// ─── Excluir proposta (hard delete — rascunho sem histórico) ────────
//
// Propostas em `draft` ainda não foram enviadas; excluir = remover
// completamente. Para propostas enviadas/aceitas, futuro PR pode
// migrar para soft-delete (manter histórico jurídico). Por ora só
// aceita DELETE em rascunho.
export interface DeleteProposalInput {
  id: string;
  lead_id: string;
}

export function useDeleteProposal() {
  const qc = useQueryClient();
  return useMutation<void, Error, DeleteProposalInput>({
    mutationFn: async (input) => {
      const { error } = await supabase
        .from('proposals')
        .delete()
        .eq('id', input.id)
        .eq('status', 'draft');
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: PROPOSALS_KEYS.byLead(variables.lead_id) });
    },
  });
}
